import { Icon } from "@iconify/react/dist/iconify.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiCall } from "../../Apis/apiCall";
import LoadingComponent from "../../components/ui/LoadingComponent";
import TableDataNoAvailable from "../../components/ui/TableDataNoAvailable";
import PaginationControls from "../../components/ui/PaginationControls";
import { TextWrap } from "../../utils/textWrap";
import Breadcrumb from "../../components/Breadcrumb";
import CardContainer from "../../components/ui/CardContainer";
import PerPageDropdown from "../../components/ui/PerPageDropdown";
import ErrorAlert from "../../components/ui/ErrorAlert";
import SortIcons from "../../components/ui/SortIcons";
import { AddButton } from "../../components/ui/Button";
import { handleApiResponse, handleCustomResponse } from "../../utils/response";
import InfoModal from "../../components/ui/InfoeModal";

const WorkFlowAssignList = () => {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalType, setModalType] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [workflowId, setWorkflowId] = useState(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const [showInfoModal, setShowInfoModal] = useState(false); // 👈 NEW: Info Modal State
  const searchInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [loading]);

  const fetchWorkflows = async (
    page = 1,
    perPage = 10,
    search = "",
    sortBy = null,
    sortOrder = "asc"
  ) => {
    setLoading(true);
    setError(null);
    try {
      const fieldMapping = {
        workflow_name: "name",
        branch_name: "branch_name",
        location_name: "location_name",
        created_at: "created_at",
      };

      const queryParams = new URLSearchParams({
        page,
        per_page: perPage,
        ...(search && { search: search }),
        ...(sortBy && { sort_by: fieldMapping[sortBy] || sortBy }),
        ...(sortOrder && { sort_order: sortOrder }),
      });

      const response = await apiCall(
        "get",
        `/get-assigned-workflows-v2?${queryParams.toString()}`,
        null,
        navigate
      );

      if (response?.data?.success) {
        handleApiResponse(response.data, "fetch workflows.", "GET");
        const { data = [], pagination = {} } = response.data;
        const {
          current_page = page,
          per_page = perPage,
          total = 0,
        } = pagination;
        const mappedWorkflows = data.map((workflow, index) => ({
          ...workflow,
          id: workflow.id || index + 1,
          workflow_name: workflow.name || "-",
          branch_name: workflow.branch_name || "-",
          location_name: workflow.location_name || "-",
          created_at: workflow.created_at
            ? new Date(workflow.created_at).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "-",
        }));
        setWorkflows(mappedWorkflows);
        setPagination({
          currentPage: parseInt(current_page, 10),
          perPage: parseInt(per_page, 10),
          total,
        });
      } else {
        throw new Error("No workflows found.");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch workflows.";
      setError(errorMessage);
      setWorkflows([]);
      setPagination((prev) => ({ ...prev, total: 0, currentPage: 1 }));

      const err = error.response?.data || error.message;
      handleApiResponse(err, "Failed to fetch workflows.", "GET");
    } finally {
      setLoading(false);
    }
  };

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const debouncedSearch = useCallback(
    debounce((query) => {
      fetchWorkflows(
        1,
        pagination.perPage,
        query,
        sortConfig.field,
        sortConfig.direction
      );
    }, 500),
    [pagination.perPage, sortConfig.field, sortConfig.direction]
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    debouncedSearch(value);
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const sortedWorkflows = useMemo(() => {
    return [...workflows].sort((a, b) => {
      if (!sortConfig.field) return 0;
      let valueA = a[sortConfig.field] ?? "";
      let valueB = b[sortConfig.field] ?? "";
      if (sortConfig.field === "created_at") {
        const dateA = valueA !== "-" ? new Date(valueA) : new Date(0);
        const dateB = valueB !== "-" ? new Date(valueB) : new Date(0);
        return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
      }
      return sortConfig.direction === "asc"
        ? valueA.toString().localeCompare(valueB.toString(), undefined, {
            sensitivity: "base",
          })
        : valueB.toString().localeCompare(valueA.toString(), undefined, {
            sensitivity: "base",
          });
    });
  }, [workflows, sortConfig]);

  useEffect(() => {
    fetchWorkflows(
      pagination.currentPage,
      pagination.perPage,
      searchQuery,
      sortConfig.field,
      sortConfig.direction
    );
  }, [
    pagination.currentPage,
    pagination.perPage,
    sortConfig.field,
    sortConfig.direction,
  ]);

  const handleOpenModal = (type, workflow = null) => {
    setModalType(type);
    setShowModal(true);
    setWorkflowId(null);
    setSelectedWorkflow(null);
    if (type === "delete" && workflow) {
      if (!workflow.id || isNaN(workflow.id)) {
        handleCustomResponse("error", `Cannot ${type}: Invalid Workflow ID.`);

        return;
      }
      setSelectedWorkflow(workflow);
      setWorkflowId(workflow.id);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalType("");
    setWorkflowId(null);
    setSelectedWorkflow(null);
  };

  const handleDeleteWorkflow = async () => {
    if (!workflowId || isNaN(workflowId)) {
      handleCustomResponse("error", "Cannot delete: Invalid Workflow ID.");

      return;
    }

    try {
      setLoading(true);
      const response = await apiCall(
        "delete",
        `/delete-workflow-location-assignment/${workflowId}`,
        null,
        navigate
      );

      if (response?.data?.success) {
        handleApiResponse(
          response?.data,
          "Workflow deleted successfully!",
          "DELETE"
        );

        await fetchWorkflows(
          pagination.currentPage,
          pagination.perPage,
          searchQuery
        );
        handleCloseModal();
      } else {
        handleApiResponse(
          response?.data,
          "Failed to delete workflow.",
          "DELETE"
        );
      }
    } catch (error) {
      const err = error.response?.data || error.message;

      handleApiResponse(err, "Failed to delete workflow.", "DELETE");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (modalType === "delete") handleDeleteWorkflow();
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handlePerPageChange = (value) => {
    setPagination((prev) => ({ ...prev, perPage: value, currentPage: 1 }));
  };

  // 👈 NEW: Info Modal Handlers
  const handleInfoClick = () => {
    setShowInfoModal(true);
  };

  const handleInfoClose = () => {
    setShowInfoModal(false);
  };

  if (error) {
    return (
      <ErrorAlert
        error={error}
        onRetry={() =>
          fetchWorkflows(
            pagination.currentPage,
            pagination.perPage,
            searchQuery
          )
        }
      />
    );
  }

  const headerContent = (
    <div className="container-fluid">
      <div className="row align-items-center">
        <div className="col-12 col-md-6 mb-3 mb-md-0">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <div className="navbar-search flex-grow-1">
              <input
                type="text"
                className="form-control h-40-px w-100"
                name="search"
                placeholder="Search by workflow name..."
                value={searchQuery}
                onChange={handleSearchChange}
                ref={searchInputRef}
                aria-label="Search workflows"
              />
              <i className="bi bi-search icon"></i>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 d-flex align-items-center justify-content-between">
          <PerPageDropdown
            perPage={pagination.perPage}
            handlePerPageChange={handlePerPageChange}
          />
          <div className="d-flex align-items-center gap-2"> {/* 👈 UPDATED: Added Info Button */}
            <button
              type="button"
              className="bg-info-50 text-info-600 bg-hover-info-100 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="Information about the Workflow Assign List Page"
              onClick={handleInfoClick}
              aria-label="Page Information"
            >
              <Icon icon="lucide:info" className="menu-icon" />
            </button>
            <AddButton
              label="Assign Workflow"
              permissionKey="assign_workflow"
              onClick={() => navigate("/workflow-assign")}
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Breadcrumb title="WorkFlow Assign List" />
      <CardContainer
        headerContent={headerContent}
        paginationControls={
          <PaginationControls
            pagination={pagination}
            handlePageChange={handlePageChange}
          />
        }
        loading={loading}
      >
        <div
          className="position-relative"
          style={{
            opacity: loading ? 0.4 : 1,
            pointerEvents: loading ? "none" : "auto",
          }}
        >
          {sortedWorkflows.length === 0 && !loading ? (
            <TableDataNoAvailable
              message={
                searchQuery
                  ? "No workflows match your search."
                  : "No workflows found."
              }
            />
          ) : (
            <table
              className="table bordered-table sm-table mb-0 table-hover"
              aria-label="Workflows table"
            >
              <thead>
                <tr>
                  <th>S.No</th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("workflow_name")}
                    role="button"
                    aria-label={`Sort by workflow name, currently sorted ${
                      sortConfig.field === "workflow_name"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Workflow Name
                      <SortIcons
                        field="workflow_name"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("branch_name")}
                    role="button"
                    aria-label={`Sort by branch name, currently sorted ${
                      sortConfig.field === "branch_name"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Branch Name
                      <SortIcons field="branch_name" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("location_name")}
                    role="button"
                    aria-label={`Sort by location name, currently sorted ${
                      sortConfig.field === "location_name"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Location Name
                      <SortIcons
                        field="location_name"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("created_at")}
                    role="button"
                    aria-label={`Sort by created date, currently sorted ${
                      sortConfig.field === "created_at"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Created Date
                      <SortIcons field="created_at" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th scope="col" className="text-center">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedWorkflows.map((workflow, index) => (
                  <tr key={workflow.id || index}>
                    <td>
                      {(pagination.currentPage - 1) * pagination.perPage +
                        index +
                        1}
                    </td>
                    <TextWrap
                      location={workflow.workflow_name}
                      onClick={() => navigate("/workflows")}
                      link={true}
                    />
                    <TextWrap
                      location={workflow.branch_name}
                      link={true}
                      onClick={() => navigate("/branch-list")}
                    />
                    <TextWrap
                      location={workflow.location_name}
                      onClick={() => navigate("/branch-locations")}
                      link={true}
                    />
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {workflow.created_at}
                      </span>
                    </td>
                    <td className="text-center">
                      <div className="d-flex align-items-center gap-10 justify-content-center">
                        <button
                          className="bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          onClick={() => handleOpenModal("delete", workflow)}
                          title={`Delete workflow ${workflow.workflow_name}`}
                          aria-label={`Delete workflow ${
                            workflow.workflow_name || "-"
                          }`}
                        >
                          <Icon
                            icon="fluent:delete-24-regular"
                            className="menu-icon"
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {loading && (
          <div
            className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
            style={{
              zIndex: 10,
              borderRadius: "10px",
              padding: "20px",
            }}
          >
            <LoadingComponent />
          </div>
        )}
      </CardContainer>

      {/* 👈 EXISTING: Main Modal (Delete) */}
      <div
        className={`modal fade ${showModal ? "show d-block" : ""}`}
        tabIndex="-1"
        role="dialog"
        style={{ display: showModal ? "block" : "none",backgroundColor: showModal ? "rgba(0,0,0,0.5)" : "" 
 }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Deletion</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseModal}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <p className="text-neutral-900">
                Are you sure you want to delete{" "}
                <strong>
                  {selectedWorkflow?.workflow_name || "this workflow"}
                </strong>
                ?
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseModal}
                aria-label="Cancel"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger d-flex align-items-center"
                onClick={handleSubmit}
                disabled={loading}
                aria-label={`Delete workflow ${
                  selectedWorkflow?.workflow_name || "this workflow"
                }`}
              >
                {loading ? "Please wait..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showModal && <div className="no-modal fade show"></div>}

      {/* 👈 NEW: Info Modal - EXACTLY SAME AS Previous Pages */}
     <InfoModal
  isOpen={showInfoModal}
  onClose={handleInfoClose}
  title="Workflow Assignment Management - Complete Overview"
>
  <p className="text-neutral-900 mb-4">
    This page is the <strong>central dashboard</strong> for managing which <strong>Workflows</strong> are assigned to which <strong>Branches</strong> and <strong>Locations</strong>. 
    You can see all active assignments and add or remove them easily.
  </p>

  <h6 className="fw-bold text-primary-600 mb-3">What You See in the List</h6>
  <ul className="list-group list-group-flush mb-4">
    <li className="list-group-item"><strong>Workflow Name</strong> – The name of the assigned workflow</li>
    <li className="list-group-item"><strong>Branch</strong> – Which branch this workflow is linked to</li>
    <li className="list-group-item"><strong>Location</strong> – Specific location within the branch (if applicable)</li>
    <li className="list-group-item"><strong>Assigned On</strong> – Date when it was assigned</li>
  </ul>

  <h6 className="fw-bold text-primary-600 mb-3">Assign New Workflow – How It Works</h6>
  <p className="small text-neutral-600 mb-2">Go to the "Assign Workflow" tab and follow these steps:</p>
  <ul className="list-group list-group-flush mb-4">
    <li className="list-group-item"><strong>Select Workflow</strong> – Choose from available workflows</li>
    <li className="list-group-item"><strong>Select Branch</strong> – Pick the branch where it will apply</li>
    <li className="list-group-item"><strong>Select Location (Optional)</strong> – Assign to a specific location inside the branch</li>
    <li className="list-group-item"><strong>Click "Assign"</strong> → Instantly active!</li>
  </ul>

  <h6 className="fw-bold text-primary-600 mb-3">Action Button (Per Assignment)</h6>
  <ul className="list-group list-group-flush mb-4">
    <li className="list-group-item">
      <strong>Delete (Red Trash)</strong><br/>
      Remove the workflow assignment from that branch/location<br/>
      <small className="text-muted">Workflow itself remains safe – only the assignment is removed</small>
    </li>
  </ul>

  <div className="alert alert-info small mt-4">
    <strong>Note:</strong><br/>
    • A workflow can be assigned to multiple branches/locations<br/>
    • Removing an assignment stops that workflow from running in that place<br/>
    • No "Edit" needed – just delete and re-assign with new settings if required<br/>
    • Changes take effect immediately
  </div>

  <p className="text-neutral-900 mt-4 fw-bold text-primary">
    Pro Tip: Always assign workflows right after creating them → No delays in automation!
  </p>
  <p className="text-neutral-900 small">
    Business Impact: Correct workflow assignments = Smooth operations, accurate incentives, zero manual work, and happy staff + customers.
  </p>
</InfoModal>
    </>
  );
};

export default WorkFlowAssignList;